import { buildSafeFoodContext } from "./aiMealBuilder";

const NUTRIENT_KEYS = ["potassium_mg", "phosphorus_mg", "sodium_mg", "protein_g"];
const FOOD_CONTEXT_LIMIT = 150;
const HISTORY_ITEM_LIMIT = 30;
const SAVED_MEAL_LIMIT = 12;
const MIXER_INGREDIENT_LIMIT = 12;

const FOOD_ALIAS_RULES = [
  { aliases: ["rice", "رز"], pattern: /\brice\b|رز/iu },
  { aliases: ["chicken", "دجاج"], pattern: /\bchicken\b|دجاج/iu },
  { aliases: ["cucumber", "خيار"], pattern: /\bcucumber\b|خيار/iu },
  { aliases: ["parsley", "بقدونس"], pattern: /\bparsley\b|بقدونس/iu },
  { aliases: ["tomato", "طماطم", "بندورة"], pattern: /\btomato\b|طماطم|بندورة/iu },
  { aliases: ["bulgur", "برغل"], pattern: /\bbulgur\b|برغل/iu },
  { aliases: ["mint", "نعناع"], pattern: /\bmint\b|نعناع/iu },
  { aliases: ["lemon", "ليمون"], pattern: /\blemon\b|ليمون/iu },
  { aliases: ["olive oil", "زيت زيتون"], pattern: /\bolive\s+oil\b|زيت\s+زيتون/iu },
];

export const localDateKey = (date = new Date()) => {
  const value = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(value.getTime())) return localDateKey(new Date());

  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${value.getFullYear()}-${month}-${day}`;
};

export const todayRange = (date = new Date()) => {
  const start = date instanceof Date ? new Date(date) : new Date(date);
  if (Number.isNaN(start.getTime())) return todayRange(new Date());
  start.setHours(0, 0, 0, 0);

  const end = new Date(start);
  end.setDate(end.getDate() + 1);

  return {
    start: start.toISOString(),
    end: end.toISOString(),
  };
};

const toNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const roundNutrient = (key, value) => {
  const number = toNumber(value);
  return key === "protein_g" ? Number(number.toFixed(1)) : Math.round(number);
};

const emptyTotals = () => ({
  potassium_mg: 0,
  phosphorus_mg: 0,
  sodium_mg: 0,
  protein_g: 0,
});

const compactTotals = (totals = {}) =>
  NUTRIENT_KEYS.reduce((acc, key) => {
    acc[key] = roundNutrient(key, totals[key]);
    return acc;
  }, {});

const addTotals = (acc, source = {}) => {
  NUTRIENT_KEYS.forEach((key) => {
    acc[key] += toNumber(source[key]);
  });
  return acc;
};

const safeText = (value, maxLength = 120) =>
  typeof value === "string" ? value.trim().slice(0, maxLength) : "";

const readJsonArray = (key) => {
  if (!key || typeof window === "undefined") return [];

  try {
    const saved = localStorage.getItem(key);
    const parsed = saved ? JSON.parse(saved) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    localStorage.removeItem(key);
    return [];
  }
};

const readJsonObject = (key) => {
  if (!key || typeof window === "undefined") return null;

  try {
    const saved = localStorage.getItem(key);
    const parsed = saved ? JSON.parse(saved) : null;
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : null;
  } catch {
    localStorage.removeItem(key);
    return null;
  }
};

const logDateKey = (log) => localDateKey(log?.logged_at || new Date());

const summarizeMixedMealIngredients = (log) => {
  const directIngredients = Array.isArray(log?.mixed_meal_ingredients)
    ? log.mixed_meal_ingredients
    : Array.isArray(log?.ingredients)
      ? log.ingredients
      : null;

  if (directIngredients) {
    return directIngredients
      .map((ingredient) => ({
        name: safeText(ingredient?.name || ingredient?.food_name || ingredient?.food?.name || ""),
        quantity: toNumber(ingredient?.quantity || 1) || 1,
      }))
      .filter((ingredient) => ingredient.name)
      .slice(0, MIXER_INGREDIENT_LIMIT);
  }

  const servingSize = safeText(log?.serving_size || "", 500);
  const markerMatch = servingSize.match(/ingredients:\s*(.+)$/iu);
  if (!markerMatch) return [];

  return markerMatch[1]
    .split(/\s*,\s*/)
    .map((part) => {
      const match = part.match(/^(.+?)\s+x\s*(\d+(?:\.\d+)?)$/iu);
      return {
        name: safeText(match ? match[1] : part),
        quantity: match ? toNumber(match[2]) || 1 : 1,
      };
    })
    .filter((ingredient) => ingredient.name)
    .slice(0, MIXER_INGREDIENT_LIMIT);
};

const summarizeHistoryLog = (log) => {
  const ingredients = summarizeMixedMealIngredients(log);

  return {
    name: safeText(log.food_name || log.foods?.name || log.name || "Unknown food"),
    quantity: toNumber(log.quantity || 1) || 1,
    serving_size: safeText(log.serving_size || log.foods?.serving_size || "", 160),
    category: safeText(log.category || log.foods?.category || "", 80),
    ingredients,
  };
};

const summarizeTodayHistory = (logs, date) => {
  const dateKey = localDateKey(date);
  const todayLogs = (logs || []).filter((log) => logDateKey(log) === dateKey);
  const totals = todayLogs.reduce((acc, log) => addTotals(acc, log), emptyTotals());

  return {
    available: true,
    date: dateKey,
    items: todayLogs.slice(0, HISTORY_ITEM_LIMIT).map(summarizeHistoryLog),
    totals: compactTotals(totals),
  };
};

const getFoodName = (food) => safeText(food?.name || food?.name_en || food?.name_ar || "");

const summarizeMixerItems = (items = []) => {
  const totals = emptyTotals();
  const ingredients = [];

  (items || []).forEach((item) => {
    const quantity = toNumber(item.quantity || 1) || 1;
    const food = item.food || {};
    ingredients.push({
      name: getFoodName(food),
      quantity,
    });

    NUTRIENT_KEYS.forEach((key) => {
      totals[key] += toNumber(food[key]) * quantity;
    });
  });

  return {
    ingredients: ingredients.filter((ingredient) => ingredient.name).slice(0, MIXER_INGREDIENT_LIMIT),
    totals: compactTotals(totals),
  };
};

const summarizeCurrentMixer = (key) => {
  const draft = readJsonObject(key);
  if (!draft || !Array.isArray(draft.items) || draft.items.length === 0) return null;

  const summary = summarizeMixerItems(draft.items);
  return {
    mealName: safeText(draft.mealName || "Mixed Meal"),
    ingredients: summary.ingredients,
    totals: summary.totals,
  };
};

const summarizeSavedMeals = (key) =>
  readJsonArray(key)
    .slice(0, SAVED_MEAL_LIMIT)
    .map((meal) => {
      const fallback = summarizeMixerItems(meal.items || []);
      return {
        name: safeText(meal.name || "Mixed Meal"),
        ingredientCount: Array.isArray(meal.items) ? meal.items.length : 0,
        ingredients: summarizeMixerItems(meal.items || []).ingredients,
        totals: compactTotals(meal.totals || fallback.totals),
      };
    })
    .filter((meal) => meal.name);

const normalizeAliasText = (value) =>
  String(value || "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u064b-\u065f\u0670]/g, "")
    .replace(/[^\p{L}\p{N}\s]/gu, " ")
    .replace(/\s+/g, " ")
    .trim();

const foodNameFields = (food) =>
  [food.name, food.name_en, food.name_ar].map(normalizeAliasText).filter(Boolean);

const foodHasName = (food, name) =>
  foodNameFields(food).some((foodName) => foodName === normalizeAliasText(name));

const foodHasText = (food, text) =>
  foodNameFields(food).some((foodName) => foodName.includes(normalizeAliasText(text)));

const getFoodAliases = (food) => {
  const aliases = [];

  if (foodHasText(food, "white rice") || foodHasText(food, "brown rice")) {
    aliases.push("rice", "رز");
  }
  if (foodHasText(food, "white rice")) aliases.push("white rice", "رز أبيض");
  if (foodHasText(food, "brown rice")) aliases.push("brown rice", "رز بني");
  if (foodHasText(food, "chicken breast")) aliases.push("chicken", "grilled chicken", "دجاج");
  if (foodHasText(food, "cucumber") || foodHasText(food, "خيار")) aliases.push("cucumber", "خيار");
  if (foodHasName(food, "tomato") || foodHasName(food, "طماطم") || foodHasName(food, "بندورة")) {
    aliases.push("tomato", "طماطم", "بندورة");
  }
  if (foodHasText(food, "olive oil") || foodHasText(food, "زيت زيتون")) {
    aliases.push("olive oil", "زيت زيتون");
  }

  return aliases;
};

const summarizeFoodContext = (foods = []) =>
  buildSafeFoodContext(foods)
    .slice(0, FOOD_CONTEXT_LIMIT)
    .map((food) => ({
      id: food.id ?? null,
      name: safeText(food.name || food.name_en || food.name_ar || ""),
      name_en: safeText(food.name_en || ""),
      name_ar: safeText(food.name_ar || ""),
      aliases: [...new Set(getFoodAliases(food))].slice(0, 6),
    }))
    .filter((food) => food.name || food.name_en || food.name_ar);

export const buildAssistantAppContext = ({
  guestMode,
  userId,
  foods = [],
  registeredTodayLogs = [],
  date = new Date(),
} = {}) => {
  const dateKey = localDateKey(date);
  const historyLogs = guestMode ? readJsonArray("guestFoodLogs") : registeredTodayLogs;
  const mixerKey = guestMode ? "ddp-meal-mixer-guest" : userId ? `ddp-meal-mixer-${userId}` : null;
  const savedMealsKey = guestMode
    ? "ddp-saved-mixed-meals-guest"
    : userId
      ? `ddp-saved-mixed-meals-${userId}`
      : null;
  const todayHistory = summarizeTodayHistory(historyLogs, date);

  return {
    todayHistory,
    todayTotals: todayHistory.totals,
    currentMixer: summarizeCurrentMixer(mixerKey),
    savedMixedMeals: summarizeSavedMeals(savedMealsKey),
    foodContext: summarizeFoodContext(foods),
    todayDate: dateKey,
  };
};
